// Crops, meals and buffs. Each crop grows only in its own ring (pots/greenhouses come later).
import type { ItemId } from './items.ts';

export type CropDef = {
  key: string;
  name: string;
  ring: number; // 0 = Greenwood
  produce: ItemId;
  seed: ItemId;
  yield: [number, number]; // produce per harvest (min, max)
  /** Art keys for the 4 stages: sprout, young, grown, ripe. */
  art: [string, string, string, string];
  stageSecs: number; // world-clock seconds per stage
};

export const CROPS: CropDef[] = [
  { key: 'wheat', name: 'Wheat', ring: 0, produce: 'wheat', seed: 'wheat_seeds', yield: [2, 3], art: ['wheat_sprout', 'wheat_young', 'wheat_grown', 'wheat_ripe'], stageSecs: 110 },
  { key: 'carrot', name: 'Carrot', ring: 0, produce: 'carrot', seed: 'carrot_seeds', yield: [2, 3], art: ['crop_sprout', 'crop_young', 'crop_grown', 'carrot_ripe'], stageSecs: 100 },
  { key: 'onion', name: 'Onion', ring: 0, produce: 'onion', seed: 'onion_seeds', yield: [2, 3], art: ['crop_sprout', 'crop_young', 'crop_grown', 'onion_ripe'], stageSecs: 100 },
  { key: 'beet', name: 'Beet', ring: 1, produce: 'beet', seed: 'beet_seeds', yield: [2, 3], art: ['crop_sprout', 'crop_young', 'crop_grown', 'beet_ripe'], stageSecs: 120 },
  { key: 'cranberry', name: 'Cranberry', ring: 1, produce: 'cranberry', seed: 'cranberry_seeds', yield: [3, 5], art: ['crop_sprout', 'crop_young', 'bush_grown', 'cranberry_ripe'], stageSecs: 130 },
  { key: 'pumpkin', name: 'Pumpkin', ring: 2, produce: 'pumpkin', seed: 'pumpkin_seeds', yield: [1, 2], art: ['crop_sprout', 'crop_young', 'crop_grown', 'pumpkin_ripe'], stageSecs: 150 },
  { key: 'squash', name: 'Squash', ring: 2, produce: 'squash', seed: 'squash_seeds', yield: [1, 2], art: ['crop_sprout', 'crop_young', 'crop_grown', 'squash_ripe'], stageSecs: 140 },
  { key: 'emberberry', name: 'Emberberry', ring: 3, produce: 'emberberry', seed: 'emberberry_seeds', yield: [3, 4], art: ['crop_sprout', 'crop_young', 'bush_grown', 'ember_ripe'], stageSecs: 170 },
];

/** Crop objects take ids from here: crop i, stage s (0-3) -> CROP_BASE + i * 4 + s. */
export const CROP_BASE = 40;
export const cropObj = (i: number, stage: number) => CROP_BASE + i * 4 + stage;
export const cropOf = (obj: number): { crop: CropDef; index: number; stage: number } | null => {
  const k = obj - CROP_BASE;
  if (k < 0 || k >= CROPS.length * 4) return null;
  return { crop: CROPS[Math.floor(k / 4)], index: Math.floor(k / 4), stage: k % 4 };
};

// ---------------------------------------------------------------- buffs

export type BuffId = 'regen' | 'hearty' | 'swift' | 'strong' | 'gatherer' | 'clarity' | 'ward';

export const BUFFS: Record<BuffId, { name: string; text: string; color: string }> = {
  regen: { name: 'Well fed', text: 'heal 2 HP/s, even in a fight', color: '#7ac87a' },
  hearty: { name: 'Hearty', text: '+20 max HP', color: '#e07a5a' },
  swift: { name: 'Swift', text: '+15% walking speed', color: '#7ab0e0' },
  strong: { name: 'Strong', text: '+25% damage', color: '#e0b85a' },
  gatherer: { name: 'Nimble hands', text: 'gather 50% faster', color: '#b08ae0' },
  clarity: { name: 'Clarity', text: 'mana comes back twice as fast', color: '#6a9ae0' },
  ward: { name: 'Warded', text: 'a ward soaks up damage', color: '#e0e0ff' },
};

/** Max HP for every distinct food you've ever eaten (Spice of Life). */
export const VARIETY_HP = 2;
export const BUFF_SECS = 300;
